import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Shield, Target, TrendingUp, PieChart, BarChart3, Layers, CheckCircle2, Clock } from 'lucide-react';

export const FundMasterBenefitsSection = () => {
  const benefits = [
    {
      icon: <TrendingUp className="w-8 h-8" />,
      title: "Index Beating Returns",
      description: "Designed to deliver consistent returns that beat the index through strategic fund selection.",
      color: "blue"
    },
    {
      icon: <Shield className="w-8 h-8" />,
      title: "Lower Cost Advantage",
      description: "Focus on Direct Plans of Mutual Funds and SIFs to minimize costs and maximize returns.",
      color: "green"
    },
    {
      icon: <PieChart className="w-8 h-8" />,
      title: "Core & Satellite Strategy",
      description: "Strategic core allocation for market replication with tactical satellite positioning for outperformance.",
      color: "blue"
    },
    {
      icon: <Clock className="w-8 h-8" />,
      title: "Long-Term Capital Growth",
      description: "Focused portfolio approach designed for sustained wealth creation over extended periods.",
      color: "green"
    },
    {
      icon: <BarChart3 className="w-8 h-8" />,
      title: "Reduced Human Bias",
      description: "Rule-based ETF integration and systematic approach to minimize emotional investment decisions.",
      color: "blue"
    },
    {
      icon: <Layers className="w-8 h-8" />,
      title: "Comprehensive Diversification",
      description: "Extensive use of mutual funds to diversify and replicate different segments of the market.",
      color: "green"
    }
  ];

  const investmentProcess = [
    {
      step: "01",
      title: "Strategic Core Allocation",
      description: "Core portfolio designed to replicate market returns through diversified fund selection."
    },
    {
      step: "02",
      title: "Tactical Satellite Positioning",
      description: "Opportunistic allocations based on market conditions to add outperformance."
    },
    {
      step: "03",
      title: "Direct Plans Selection",
      description: "Focus on lower-cost Direct Plans of Mutual Funds and SIFs for cost efficiency."
    },
    {
      step: "04",
      title: "Risk Management",
      description: "Long-term investment focus with derivative-based hedging available when necessary."
    }
  ];

  return (
    <section id="benefits" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-slate-800 mb-6">
            Why Choose <span className="bg-gradient-to-r from-blue-600 to-green-600 bg-clip-text text-transparent">FundMaster</span>?
          </h2>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto leading-relaxed">
            Experience superior returns through our innovative Core & Satellite approach combining the best of active and passive investing.
          </p>
        </div>

        {/* Key Benefits Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-20">
          {benefits.map((benefit, index) => (
            <Card key={index} className={`hover:shadow-2xl transition-all duration-300 group cursor-pointer border-${benefit.color}-100`}>
              <CardHeader className="text-center">
                <div className={`mx-auto p-4 rounded-2xl bg-${benefit.color}-50 text-${benefit.color}-600 group-hover:scale-110 transition-transform duration-300`}>
                  {benefit.icon}
                </div>
                <CardTitle className="text-xl font-bold text-slate-800 group-hover:text-blue-700 transition-colors">
                  {benefit.title}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-slate-600 text-center leading-relaxed">
                  {benefit.description}
                </p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Investment Process */}
        <div className="mb-16">
          <h3 className="text-2xl md:text-3xl font-bold text-slate-800 text-center mb-12">
            Our Investment <span className="text-blue-600">Process</span>
          </h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {investmentProcess.map((process, index) => (
              <div key={index} className="relative">
                <Card className="bg-gradient-to-br from-blue-50 to-green-50 border-blue-200 hover:shadow-xl transition-all duration-300">
                  <CardContent className="p-6 text-center">
                    <div className="text-4xl font-bold text-blue-600 mb-4">{process.step}</div>
                    <h4 className="text-lg font-semibold text-slate-800 mb-3">{process.title}</h4>
                    <p className="text-sm text-slate-600 leading-relaxed">{process.description}</p>
                  </CardContent>
                </Card>
                {index < investmentProcess.length - 1 && (
                  <div className="hidden lg:block absolute top-1/2 -right-3 w-6 h-6 bg-blue-500 rounded-full transform -translate-y-1/2 z-10">
                    <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-2 h-2 bg-white rounded-full"></div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Competitive Advantages */}
        <Card className="bg-gradient-to-r from-slate-50 via-blue-50 to-green-50 border-blue-200 shadow-2xl">
          <CardHeader className="text-center">
            <CardTitle className="text-2xl font-bold text-slate-800 mb-4">
              Competitive Advantages
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="space-y-4">
                <h4 className="text-lg font-semibold text-slate-800 mb-4">Investment Excellence</h4>
                <div className="space-y-3">
                  <div className="flex items-center space-x-3">
                    <CheckCircle2 className="w-5 h-5 text-green-600" />
                    <span className="text-slate-700">Nifty 50 Hybrid Composite Debt 50:50 Benchmark</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <CheckCircle2 className="w-5 h-5 text-blue-600" />
                    <span className="text-slate-700">Medium to Long-term Investment Horizon</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <CheckCircle2 className="w-5 h-5 text-green-600" />
                    <span className="text-slate-700">Comprehensive Mutual Fund Universe Coverage</span>
                  </div>
                </div>
              </div>
              <div className="space-y-4">
                <h4 className="text-lg font-semibold text-slate-800 mb-4">Strategy & Risk Management</h4>
                <div className="space-y-3">
                  <div className="flex items-center space-x-3">
                    <CheckCircle2 className="w-5 h-5 text-blue-600" />
                    <span className="text-slate-700">Core & Satellite Approach</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <CheckCircle2 className="w-5 h-5 text-green-600" />
                    <span className="text-slate-700">Direct Plans Cost Efficiency</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <CheckCircle2 className="w-5 h-5 text-blue-600" />
                    <span className="text-slate-700">Derivative-based Hedging Capabilities</span>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </section>
  );
};